import { useCallback, useEffect, useRef } from "react";
import { call, select } from "redux-saga/effects";

import { TokenManagerSingleton } from "authentication/TokenManager";
import { useSelector } from "common/utilities/react-redux";
import { RequestError } from "common/utilities/requestError";
import { getIdToken } from "domains/authentication/selectors";
import { getSiteLanguage } from "domains/language/selectors";

type HttpMethod = "DELETE" | "GET" | "PATCH" | "POST" | "PUT";

type Options = {
  body?: object;
  headers?: Record<string, string>;
  idToken?: string;
  method?: HttpMethod;
  queryParams?: Record<string, QueryValue>;
};

type QueryValue = boolean | null | number | string | string[] | undefined;

export function* apiFetch<T>(endpoint: string, options: Options = {}) {
  const response: Response = yield call(baseApiFetch, endpoint, options);
  if (!contentIsJson(response)) return undefined;
  const body: T = yield call(() => response.json());
  return body;
}

export function* fileApiFetch(endpoint: string, options: Options = {}) {
  const response: Response = yield call(baseApiFetch, endpoint, options);
  const body: Blob = yield call(() => response.blob());
  return body;
}

function* baseApiFetch(endpoint: string, options: Options = {}) {
  const idToken: ReturnType<typeof getIdToken> = yield select(getIdToken);
  const siteLanguage: ReturnType<typeof getSiteLanguage> = yield select(getSiteLanguage);
  const response: Response = yield call(tryFetch, endpoint, {
    ...options,
    idToken: options.idToken ?? idToken,
    language: siteLanguage,
  });
  return response;
}

export const useFetch = () => {
  const idToken = useSelector(getIdToken);
  const siteLanguage = useSelector(getSiteLanguage);

  // Access values without breaking reference equality
  const idTokenRef = useRef(idToken);
  const siteLanguageRef = useRef(siteLanguage);

  useEffect(() => {
    idTokenRef.current = idToken;
  }, [idToken]);

  useEffect(() => {
    siteLanguageRef.current = siteLanguage;
  }, [siteLanguage]);

  return useCallback(
    (endpoint: string, options?: Options) =>
      tryFetch(endpoint, {
        ...options,
        idToken: options?.idToken ?? idTokenRef.current,
        language: siteLanguageRef.current,
      }),
    [],
  );
};

type FormDataUploadOptions = Omit<Options, "body"> & { body: FormData };

export function* formDataUploadApiFetch<T>(endpoint: string, options: FormDataUploadOptions) {
  const idToken: ReturnType<typeof getIdToken> = yield select(getIdToken);
  const siteLanguage: ReturnType<typeof getSiteLanguage> = yield select(getSiteLanguage);

  const response: Response = yield call(tryFetch, endpoint, {
    ...options,
    idToken: options.idToken ?? idToken,
    language: siteLanguage,
    method: options.method ?? "POST",
  });

  if (!contentIsJson(response)) return undefined;
  const payload: T = yield call(() => response.json());
  return payload;
}

const tryFetch = (endpoint: string, options: Options & { language?: string }) => {
  const { idToken, body, headers, language, method = "GET", queryParams = {} } = options;

  const url = new URL(`${import.meta.env.VITE_API_URL}/v1/${endpoint}`);
  Object.entries(queryParams).forEach(([key, value]) => {
    if (value === undefined || value === null || (Array.isArray(value) && value.length === 0)) return;
    url.searchParams.append(key, Array.isArray(value) ? value.join(",") : value.toString());
  });

  const fetchWithTokenRenewalOnFailure = (token: string | undefined, renewTokensOnFailure = true): Promise<Response> =>
    fetch(url.toString(), {
      body: body === undefined || body instanceof FormData ? body : JSON.stringify(body),
      headers: {
        ...headers,
        ...(body !== undefined && !(body instanceof FormData) && { "content-type": "application/json" }),
        ...(language !== undefined && { "accept-language": language }),
        ...(token !== undefined && { "x-authorization": `Bearer ${token}` }),
        "x-api-key": import.meta.env.VITE_API_KEY,
      },
      method,
    }).then((response) => {
      if (response.ok) return response;
      if (!renewTokensOnFailure || token === undefined || response.status !== 401) throw new RequestError(response);
      return TokenManagerSingleton.renewTokens().then((idToken) => {
        if (idToken === undefined) throw new RequestError(response);
        return fetchWithTokenRenewalOnFailure(idToken, false);
      });
    });

  return fetchWithTokenRenewalOnFailure(idToken);
};

const contentIsJson = (response: Response) => {
  const contentType = response.headers.get("content-type");
  return contentType?.includes("application/json") ?? false;
};
